import { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { loginRequest } from "../authConfig";
import {
  clearE2eToken,
  isE2eSignInEnabled,
  readE2eToken,
} from "./e2eSession";

const requestAccessToken = async ({ instance, accounts, setToken }) => {
  // Check if we have a valid account
  if (!accounts || accounts.length === 0) {
    console.warn("No accounts available for token acquisition");
    localStorage.removeItem("user:token");
    return null;
  }

  const request = {
    ...loginRequest,
    account: accounts[0],
  };

  try {
    // First try silent token acquisition
    const response = await instance.acquireTokenSilent(request);
    localStorage.setItem("user:token", response.accessToken);
    setToken(response.accessToken);
    return response.accessToken;
  } catch (silentError) {
    // Only attempt interactive auth if it's an InteractionRequiredAuthError
    if (silentError instanceof InteractionRequiredAuthError) {
      try {
        console.log("Silent token acquisition failed, attempting popup authentication");
        // Use select_account prompt to ensure user can re-authenticate properly
        const response = await instance.acquireTokenPopup({
          ...request,
          prompt: "select_account",
        });
        localStorage.setItem("user:token", response.accessToken);
        setToken(response.accessToken);
        return response.accessToken;
      } catch (popupError) {
        console.error("Token acquisition failed:", popupError);
        // Clear potentially stale token
        localStorage.removeItem("user:token");
        return null;
      }
    } else {
      // For other errors, log and clear token
      console.error("Silent token acquisition failed:", silentError);
      localStorage.removeItem("user:token");
      return null;
    }
  }
};

const useMSAL = () => {
  const [token, setToken] = useState();
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const e2eToken = isE2eSignInEnabled() ? readE2eToken() : null;

  useEffect(() => {
    if (isAuthenticated && !e2eToken) {
      requestAccessToken({ instance, accounts, setToken });
    }
  }, [isAuthenticated, instance, accounts, e2eToken]);

  if (e2eToken) {
    return {
      isAuthenticated: true,
      token: e2eToken,
      requestAccessToken: () => clearE2eToken(),
    };
  }

  return {
    isAuthenticated,
    token,
    requestAccessToken: async () =>
      await requestAccessToken({ instance, accounts, setToken }),
  };
};

export default useMSAL;
